/**
 * A character's decision about a passage written about them.
 *
 * There is exactly one decision here, and it only goes one way: open it to everyone.
 * Taking a passage out of the book is not a button — it is a sentence said to the
 * author, who owns the manuscript and does the removing himself.
 *
 * Opening is irreversible and the dialog says so plainly. Softening that would be a lie
 * told to someone who trusted the author enough to be written about.
 *
 * The copy avoids second-person verbs: Hebrew forces a gender on them, and this is
 * addressed to a real person whose gender the book does not know.
 */

import { Button, Dialog, DialogActions, DialogContent, Menu, MenuItem, Stack, Typography, alpha } from '@mui/material'
import React, { useState } from 'react'

import { READER_PAPER_BACKGROUND_SX } from '../../styles/paperBackground'

export type ConsentAction = 'release'

interface CharacterConsentMenuProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  onChoose: (action: ConsentAction) => void
  /** The book's title font, so the choice is set in the same hand as its headings. */
  titleFontFamily?: string
}

/** Paper, ink and a hairline border — the surface the reader is already looking at. */
const bookSurfaceSx = {
  ...READER_PAPER_BACKGROUND_SX,
  color: 'var(--reader-text-primary)',
  border: '1px solid var(--reader-sidebar-divider)',
  borderRadius: '10px',
  boxShadow: '0 18px 44px rgba(58, 46, 34, 0.24)',
} as const

export const CharacterConsentMenu: React.FC<CharacterConsentMenuProps> = ({
  anchorEl,
  onClose,
  onChoose,
  titleFontFamily,
}) => {
  const [confirming, setConfirming] = useState(false)

  return (
    <>
      <Menu
        open={Boolean(anchorEl) && !confirming}
        anchorEl={anchorEl}
        onClose={onClose}
        dir="rtl"
        slotProps={{ paper: { sx: { ...bookSurfaceSx, minWidth: 260 } } }}
        MenuListProps={{ sx: { py: 0.5 } }}
      >
        <Stack sx={{ px: 2, pt: 1, pb: 1.25 }}>
          <Typography sx={{ fontSize: '0.8rem', color: 'var(--reader-text-muted)', lineHeight: 1.6 }}>
            הקטע הזה נסתר מהקוראים. ההחלטה אם ייפתח היא שלך בלבד.
          </Typography>
        </Stack>

        <MenuItem
          onClick={() => setConfirming(true)}
          sx={{
            mx: 1,
            borderRadius: '6px',
            '&:hover': { backgroundColor: 'var(--reader-hover)' },
          }}
        >
          <Stack>
            <Typography sx={{ fontSize: '0.95rem', color: 'var(--reader-text-primary)' }}>לפתוח לכולם</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'var(--reader-text-submuted)' }}>בלתי הפיך</Typography>
          </Stack>
        </MenuItem>
      </Menu>

      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        maxWidth="xs"
        fullWidth
        dir="rtl"
        BackdropProps={{ sx: { backgroundColor: alpha('#3a2e22', 0.42), backdropFilter: 'blur(2px)' } }}
        PaperProps={{ sx: { ...bookSurfaceSx, px: 1, py: 2 } }}
      >
        <DialogContent sx={{ pb: 1 }}>
          <Typography
            sx={{
              fontFamily: titleFontFamily || 'inherit',
              fontSize: '1.3rem',
              color: 'var(--reader-text-primary)',
              textAlign: 'center',
            }}
          >
            לפתוח את הקטע לכולם?
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              fontSize: '0.9rem',
              lineHeight: 1.9,
              color: 'var(--reader-text-muted)',
              textAlign: 'center',
            }}
          >
            מרגע שהקטע נפתח, כל מי שקורא את הספר רואה אותו. אי אפשר לסגור בחזרה — מה שיצא, יצא.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 1, justifyContent: 'center', gap: 1 }}>
          <Button
            onClick={() => setConfirming(false)}
            disableElevation
            sx={{
              color: 'var(--reader-text-muted)',
              textTransform: 'none',
              fontSize: '0.9rem',
              '&:hover': { backgroundColor: 'var(--reader-hover)' },
            }}
          >
            לא עכשיו
          </Button>
          <Button
            onClick={() => {
              onChoose('release')
              setConfirming(false)
              onClose()
            }}
            disableElevation
            sx={{
              color: 'var(--reader-text-primary)',
              textTransform: 'none',
              fontSize: '0.9rem',
              px: 2.5,
              border: '1px solid var(--reader-sidebar-divider)',
              borderRadius: '6px',
              '&:hover': { backgroundColor: 'var(--reader-hover)', borderColor: 'var(--ornament-stroke)' },
            }}
          >
            לפתוח
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default CharacterConsentMenu
